import React, {Component, ReactNode} from 'react';

import {ButtonView, Checkbox, Dialog} from '@gravity-ui/uikit';

import {block} from '../../utils/cn';

import './ConfirmDialog.scss';

const b = block('confirm-dialog');

export enum ActionTheme {
    Normal = 'normal',
    Action = 'action',
    Danger = 'danger',
}

const actionButtonViewByTheme: Record<ActionTheme, ButtonView> = {
    [ActionTheme.Normal]: 'normal',
    [ActionTheme.Action]: 'action',
    [ActionTheme.Danger]: 'outlined-danger',
};

export interface ConfirmDialogData {
    title?: string;
    message?: ReactNode;
    confirmation?: ReactNode;
    actionId?: string;
    actionText: string;
    actionTheme?: ActionTheme;
    actionHandler: () => void | Promise<unknown>;
    cancelText: string;
}

export interface ConfirmDialogProps {
    open: boolean;
    disabled?: boolean;
    data: ConfirmDialogData;
    onRequestClose: () => void;
    onSuccess?: (actionId?: string) => void;
    onError?: (err: Error, actionId?: string) => void;
    onCancel?: () => void;
    className?: string;
    size: 's' | 'm' | 'l';
}

interface ConfirmDialogState {
    isLoading: boolean;
    confirmed: boolean;
}

export class ConfirmDialog extends Component<ConfirmDialogProps, ConfirmDialogState> {
    static ActionTheme = ActionTheme;

    static defaultProps = {
        open: false,
        data: {},
        size: 's',
    };

    state: ConfirmDialogState = {
        isLoading: false,
        confirmed: false,
    };

    render() {
        const {open, size} = this.props;

        return (
            <Dialog open={open} onClose={this.handleCancelClick} size={size} className={b()}>
                {this.renderHeader()}
                {this.renderBody()}
                {this.renderFooter()}
            </Dialog>
        );
    }

    private renderHeader() {
        const {
            data: {title},
        } = this.props;

        return <Dialog.Header caption={title} />;
    }

    private renderBody() {
        const {className} = this.props;

        return (
            <Dialog.Body className={className}>
                {this.renderMessage()}
                {this.renderConfirmation()}
            </Dialog.Body>
        );
    }

    private renderFooter() {
        const {
            disabled,
            data: {confirmation, actionText, actionTheme, cancelText},
        } = this.props;
        const {isLoading, confirmed} = this.state;

        return (
            <Dialog.Footer
                preset="default"
                textButtonCancel={cancelText}
                textButtonApply={actionText}
                onClickButtonCancel={this.handleCancelClick}
                onClickButtonApply={this.handleActionClick}
                propsButtonApply={{
                    disabled: disabled || (Boolean(confirmation) && !confirmed),
                    view: actionTheme ? actionButtonViewByTheme[actionTheme] : 'action',
                }}
                loading={isLoading}
            />
        );
    }

    private renderMessage() {
        const {
            data: {message},
        } = this.props;

        if (!message) {
            return null;
        }

        return <div className={b('message')}>{message}</div>;
    }

    private renderConfirmation() {
        const {
            data: {confirmation},
        } = this.props;
        const {confirmed} = this.state;

        if (!confirmation) {
            return null;
        }

        return (
            <div className={b('confirmation')}>
                <Checkbox
                    checked={confirmed}
                    onUpdate={this.handleConfirmUpdate}
                    content={confirmation}
                />
            </div>
        );
    }

    private handleConfirmUpdate = (confirmed: boolean) => this.setState({confirmed});

    private handleActionClick = () => {
        const {
            data: {actionId, actionHandler},
            onSuccess,
            onError,
            onRequestClose,
        } = this.props;

        if (actionHandler) {
            this.setState({isLoading: true});

            Promise.resolve(actionHandler())
                .then(() => {
                    this.setState({isLoading: false});

                    onRequestClose();

                    if (onSuccess) {
                        onSuccess(actionId);
                    }
                })
                .catch((error) => {
                    this.setState({isLoading: false});

                    if (onError) {
                        onError(error, actionId);
                    }

                    throw error;
                });
        }
    };

    private handleCancelClick = () => {
        const {
            data: {actionId},
            onCancel,
            onError,
            onRequestClose,
        } = this.props;

        try {
            onRequestClose();
            onCancel?.();
        } catch (error) {
            onError?.(error as Error, actionId);

            throw error;
        }
    };
}
